import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, type EstadoDeAyuda, type RolDeUsuario, type TipoDeAyuda } from '../servicios/api'

/**
 * Ayuda en la plataforma (RF-1700): qué recorridos, TIPs y novedades ya vio el usuario, si quiere TIPs, y
 * la petición de lanzar el recorrido desde el menú «Ayuda». Lo comparten el recorrido guiado, los TIPs y el
 * menú, para que los tres digan lo mismo.
 *
 * Lo visto vive en el servidor (RF-1707). Cada registro se aplica primero aquí y después se manda: si la
 * petición falla, en esta sesión no se vuelve a mostrar, y el peor caso es que reaparezca en el siguiente
 * ingreso. La ayuda nunca bloquea el trabajo (RN-68), así que ese fallo no se le muestra al usuario.
 *
 * Mientras no hay estado —cargando, o el servidor no respondió— no arranca nada solo y no sale ningún TIP:
 * ante la duda, no estorbar. El recorrido sí se puede lanzar a mano desde «Ayuda».
 */
export interface ValorDeAyuda {
  rol: RolDeUsuario | null
  /** Null mientras carga o si el servidor no respondió. */
  estado: EstadoDeAyuda | null
  /** Falso mientras no hay estado. */
  mostrarTips: boolean
  vioRecorrido: (clave: string, version: number) => boolean
  marcarRecorrido: (clave: string, version: number) => void
  vioTip: (clave: string) => boolean
  marcarTip: (clave: string) => void
  vioNovedad: (clave: string) => boolean
  marcarNovedad: (clave: string) => void
  /** Verdadero si el TIP del día ya se cerró hoy (fecha local). */
  tipDelDiaCerradoHoy: boolean
  cerrarTipDelDia: () => void
  cambiarMostrarTips: (mostrar: boolean) => Promise<void>
  /** El recorrido que pidieron desde «Ayuda»: `tema` null es el completo. Null si nadie lo pidió. */
  recorridoSolicitado: { tema: string | null } | null
  lanzarRecorrido: (tema?: string) => void
  cerrarRecorridoSolicitado: () => void
}

const nada = () => {}

/** Fuera del proveedor (pruebas de una página suelta) la ayuda está inerte: no muestra ni registra nada. */
const AYUDA_INERTE: ValorDeAyuda = {
  rol: null,
  estado: null,
  mostrarTips: false,
  vioRecorrido: () => true,
  marcarRecorrido: nada,
  vioTip: () => true,
  marcarTip: nada,
  vioNovedad: () => true,
  marcarNovedad: nada,
  tipDelDiaCerradoHoy: true,
  cerrarTipDelDia: nada,
  cambiarMostrarTips: async () => {},
  recorridoSolicitado: null,
  lanzarRecorrido: nada,
  cerrarRecorridoSolicitado: nada,
}

const Contexto = createContext<ValorDeAyuda>(AYUDA_INERTE)

export const useAyuda = (): ValorDeAyuda => useContext(Contexto)

/** La fecha local de hoy como yyyy-MM-dd: el TIP del día se cierra por día del usuario, no por día UTC. */
export function hoyLocal(ahora: Date = new Date()): string {
  const dos = (n: number) => String(n).padStart(2, '0')
  return `${ahora.getFullYear()}-${dos(ahora.getMonth() + 1)}-${dos(ahora.getDate())}`
}

export function ProveedorDeAyuda({ rol, children }: { rol: RolDeUsuario; children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoDeAyuda | null>(null)
  const [recorridoSolicitado, setRecorridoSolicitado] = useState<{ tema: string | null } | null>(null)

  useEffect(() => {
    let vigente = true
    api.obtenerMiAyuda()
      .then((e) => { if (vigente) setEstado(e) })
      .catch(() => { /* sin estado no se muestra nada solo (ver arriba) */ })
    return () => { vigente = false }
  }, [])

  const registrar = useCallback(
    (local: (e: EstadoDeAyuda) => EstadoDeAyuda, vista: { tipo: TipoDeAyuda; clave?: string; version?: number; dia?: string }) => {
      setEstado((e) => (e ? local(e) : e))
      api.marcarAyudaVista(vista).then(setEstado).catch(() => { /* queda lo local; ver arriba */ })
    },
    [],
  )

  const valor = useMemo<ValorDeAyuda>(() => {
    const hoy = hoyLocal()
    return {
      rol,
      estado,
      mostrarTips: estado?.mostrarTips ?? false,
      vioRecorrido: (clave, version) => (estado?.recorridos[clave] ?? 0) >= version,
      marcarRecorrido: (clave, version) =>
        registrar(
          (e) => ({ ...e, recorridos: { ...e.recorridos, [clave]: Math.max(version, e.recorridos[clave] ?? 0) } }),
          { tipo: 'Recorrido', clave, version },
        ),
      vioTip: (clave) => estado?.tips.includes(clave) ?? false,
      marcarTip: (clave) =>
        registrar((e) => (e.tips.includes(clave) ? e : { ...e, tips: [...e.tips, clave] }), { tipo: 'Tip', clave }),
      vioNovedad: (clave) => estado?.novedades.includes(clave) ?? false,
      marcarNovedad: (clave) =>
        registrar((e) => (e.novedades.includes(clave) ? e : { ...e, novedades: [...e.novedades, clave] }), { tipo: 'Novedad', clave }),
      tipDelDiaCerradoHoy: estado?.tipDelDiaCerradoEl === hoy,
      cerrarTipDelDia: () => registrar((e) => ({ ...e, tipDelDiaCerradoEl: hoy }), { tipo: 'TipDelDia', dia: hoy }),
      // A diferencia de un visto, la preferencia sí se confirma: si el servidor no la guardó, vuelve a como
      // estaba y quien la cambió recibe el error para decirlo.
      cambiarMostrarTips: async (mostrar) => {
        const anterior = estado
        setEstado((e) => (e ? { ...e, mostrarTips: mostrar } : e))
        try {
          setEstado(await api.cambiarMostrarTips(mostrar))
        } catch (error) {
          setEstado(anterior)
          throw error
        }
      },
      recorridoSolicitado,
      lanzarRecorrido: (tema) => setRecorridoSolicitado({ tema: tema ?? null }),
      cerrarRecorridoSolicitado: () => setRecorridoSolicitado(null),
    }
  }, [rol, estado, recorridoSolicitado, registrar])

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}
